<script setup lang="ts">
import { useTheme } from 'vuetify'
import type { IActivityByDaysWidget } from '@/modules/dashboard/types/IActivityByDaysWidget'
import { geVerticalBarChartConfig } from '@core/libs/chartjs/chartjsConfig'
import BarChart from '@core/libs/chartjs/components/BarChart'
import { useChartColors } from '@/utils/chartColors'

interface Props {
  widgetData: IActivityByDaysWidget
  currentTarget?: string
  flat?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  flat: false,
  loading: false,
})

const vuetifyTheme = useTheme()
const { generateColors } = useChartColors()

const isTrendPositive = computed(() => props.widgetData.data.trend >= 0)
const trendColor = computed(() => isTrendPositive.value ? 'success' : 'error')
const trendIcon = computed(() => isTrendPositive.value ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line')

const formattedValue = computed(() => {
  return new Intl.NumberFormat('ru-RU').format(props.widgetData.data.value)
})

const chartLabels = computed(() => {
  return (props.widgetData.data.labels || []).map(label => String(label.name || label.id || ''))
})

const chartData = computed(() => {
  const datasets = props.widgetData.data.datasets || []
  const colors = generateColors(Math.max(datasets.length, 1))

  return {
    labels: chartLabels.value,
    datasets: datasets.map((dataset, index) => ({
      maxBarThickness: 15,
      label: dataset.label || 'действий',
      backgroundColor: colors[index],
      borderColor: 'transparent',
      borderRadius: { topRight: 15, topLeft: 15 },
      data: dataset.data || [],
    })),
  }
})

/* Конфиг как в ChartJsBarChart — тики/наклон отдаём Chart.js */
const chartOptions = computed(() => {
  const baseConfig = geVerticalBarChartConfig(vuetifyTheme.current.value)

  return {
    ...baseConfig,
    interaction: {
      mode: 'nearest',
      intersect: true,
      axis: 'x' as const,
    },
    plugins: {
      ...baseConfig.plugins,
      legend: {
        display: false,
      },
    },
  }
})
</script>

<template>
  <VCard :variant="flat ? 'flat' : undefined" class="card-widget-days" :class="[flat ? 'pa-0 bg-transparent' : 'pa-4']">
    <!-- Flat: страница активности сотрудника -->
    <div v-if="flat" class="d-flex flex-column w-100">
      <div class="text-caption font-weight-medium text-high-emphasis mb-2">
        {{ widgetData.name }}
      </div>

      <div class="chart-canvas-wrap">
        <VSkeletonLoader
          v-if="loading"
          type="image"
          class="custom-chart-skeleton bg-transparent"
        />
        <BarChart
          v-else
          :height="200"
          :chart-data="chartData"
          :chart-options="chartOptions"
        />
      </div>
    </div>

    <!-- Карточка для UniversalGraph -->
    <VRow v-else no-gutters align="center">
      <VCol cols="12" md="3" class="d-flex flex-column justify-space-between info-column pr-md-4 mb-4 mb-md-0">
        <div v-if="loading" class="d-flex flex-column justify-space-between h-100 w-100">
          <div>
            <div class="d-flex align-center gap-2 mb-2">
              <VSkeletonLoader type="text" width="80px" class="bg-transparent my-0" />
            </div>
            <div class="mb-4">
              <VSkeletonLoader type="text" width="100%" class="bg-transparent my-1" />
              <VSkeletonLoader type="text" width="60%" class="bg-transparent my-1" />
            </div>
          </div>
          <div class="d-flex align-end gap-2 mt-auto">
            <VSkeletonLoader type="text" width="70px" class="bg-transparent my-0" />
          </div>
        </div>

        <template v-else>
          <div>
            <div class="d-flex align-center gap-2 mb-2">
              <div
                v-show="widgetData.show_icon && widgetData.icon"
                class="chart-icon-wrapper"
                :style="{ background: widgetData.icon_background }"
              >
                <VIcon :icon="widgetData.icon" size="sm" />
              </div>
              <span class="text-caption font-weight-medium text-high-emphasis">{{ widgetData.name }}</span>
            </div>
            <div class="text-body-2 text-medium-emphasis mb-4 description-text">
              {{ widgetData.description }}
            </div>
          </div>

          <div class="d-flex align-end gap-2 mt-auto">
            <span class="text-h4 font-weight-bold leading-none">{{ formattedValue }}</span>
            <div class="d-flex align-center gap-0-5 mb-1">
              <VIcon
                :icon="trendIcon"
                :color="trendColor"
                size="20"
                class="trend-arrow"
              />
              <span :class="`text-body-2 font-weight-semibold text-${trendColor}`">
                {{ Math.abs(widgetData.data.trend) }}%
              </span>
            </div>
          </div>
        </template>
      </VCol>

      <VCol cols="12" md="9" class="ps-md-4 border-left-divider">
        <div class="chart-canvas-wrap chart-canvas-wrap--card">
          <VSkeletonLoader
            v-if="loading"
            type="image"
            class="custom-chart-skeleton bg-transparent"
          />
          <BarChart
            v-else
            :height="220"
            :chart-data="chartData"
            :chart-options="chartOptions"
          />
        </div>
      </VCol>
    </VRow>
  </VCard>
</template>

<style lang="scss" scoped>
.card-widget-days {
  width: 100%;
}

.info-column {
  min-height: 140px;
}

.description-text {
  line-height: 1.4;
}

.border-left-divider {
  @media (min-width: 960px) {
    border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
}

.chart-canvas-wrap {
  position: relative;
  width: 100%;
  height: 200px;

  &--card {
    height: 220px;
  }
}

.trend-arrow {
  display: inline-flex;
  align-self: center;
}

.custom-chart-skeleton {
  height: 100%;
  width: 100%;

  :deep(.v-skeleton-loader__image) {
    height: 100%;
    border-radius: 6px;
  }
}
</style>
